import { UiVariable } from 'pages/AutomationEngine/dialogs/VariableWizard/types';
import { ActionStep } from './subsections/actionSectionTypes';
import { modifyRelatedEntitiesRemoveVariable } from './subsections/ModifyRelatedEntities/modifyRelatedEntitiesRemoveVariable';
import { changeFieldValueRemoveVariable } from './subsections/ChangeFieldValue/changeFieldValueRemoveVariable';
import { removeLLMVariable } from './subsections/llm-common/helpers';
import { updateVariableRemoveVariable } from './subsections/UpdateVariable/updateVariableRemoveVariable';
import { codeStepRemoveVariable } from './subsections/CodeStep/codeStepRemoveVariable';
import { checkValueTypeUsesVariable } from './utilities';
import { createRelatedEntityRemoveVariable } from './subsections/CreateRelatedEntity/createRelatedEntityRemoveVariable';
import { DESTINATION_TYPES } from './subsections/llm-common/constants';
import { mathOperatorsRemoveVariable } from './subsections/MathOperators/mathOperatorsRemoveVariable';
import { OWNER_SUBTYPES } from './subsections/CreateRelatedEntity/constants';
import {
  scheduleActivityRemoveVariable,
  isDeletedAssignedToVariable,
  isDeletedWithDelayDelayVariable,
  isDeletedWithDelayTimeOffsetVariable,
  isDeletedOnAndAroundDateTimeOffsetVariable,
  isDeletedOnAndAroundDateDateOffsetVariable,
  isDeletedOnAndAroundDateVariable,
  isDeletedAssociationVariable,
  isDeletedTargetVariable,
} from './subsections/ScheduleActivity/scheduleActivityRemoveVariable';
import { startAutomationStepRemoveVariable } from './subsections/StartAutomation/startAutomationStepRemoveVariable';
import { modifyAutomationStepRemoveVariable } from './subsections/ModifyAutomations/modifyAutomationStepRemoveVariable';
import { archiveRecordStepRemoveVariable } from './subsections/ArchiveRecord/archiveRecordStepRemoveVariable';
import { searchRecordsStepRemoveVariable } from './subsections/SearchRecords/searchRecordsStepRemoveVariable';
import { ELEMENT_TYPES } from './subsections/MathOperators/constants';
import { TargetFieldVariable } from './subsections/MathOperators/types';
import {
  DelayStep,
  ValueOrigin,
} from 'pages/AutomationEngine/types/delay-types';
import { delayRemoveVariable } from '../Delay/delayRemoveVariable';
import { loopRemoveVariable } from '../LoopWizard/loopRemoveVariable';
import { SCHEDULE_TYPES } from './subsections/ScheduleActivity/constants';

export const checkVariableUsedInActions = (
  steps: ActionStep[],
  variable: UiVariable
) => {
  const variableName = variable.details.variable.name;

  return steps.some((step) => {
    switch (step.type) {
      case 'modify_related_entities':
        if (
          (step.details.recordSourceVariables || [])
            .map(({ label }) => label)
            .includes(variableName)
        ) {
          return true;
        }

        return step.details.fieldsToModify.some((fieldToModify) => {
          return (
            checkValueTypeUsesVariable(fieldToModify.valueType?.value) &&
            fieldToModify.variable?.value === variableName
          );
        });

      case 'change_field_value':
        return step.details.actions.some((action) => {
          return (
            checkValueTypeUsesVariable(action.changeType?.value) &&
            action.variable?.value === variableName
          );
        });

      case 'call_llm':
        return step.details.destinations.some(
          (destination) =>
            destination.destinationType === DESTINATION_TYPES.variable &&
            destination.variable?.label === variableName
        );
      case 'file_content_extraction':
      case 'audio_transcription': {
        return (
          step.details.inputValueSource?.variable?.label === variableName ||
          step.details.destinations.some(
            (destination) =>
              destination.destinationType === DESTINATION_TYPES.variable &&
              destination.variable?.label === variableName
          )
        );
      }

      case 'update_variable': {
        const inSources = step.details?.sources.some(
          (source) =>
            source?.sourceType === 'variable' &&
            source?.automationVariable?.name === variableName
        );

        return (
          step.details?.chosenVariable?.label === variableName || inSources
        );
      }

      case 'plugin_code_step':
      case 'code_step': {
        return (
          (step.details?.inputs || []).some(
            (input) =>
              input.inputType === 'variable' &&
              input.variable?.name === variableName
          ) ||
          (step.details?.outputs || []).some(
            (output) =>
              output.outputType === 'variable' &&
              output.variable?.name === variableName
          )
        );
      }

      case 'math_operator': {
        const subType = step.details.subType;
        if (
          step.details.simpleData[subType].targetField!.isVariable &&
          step.details.simpleData[subType].targetField!.value === variableName
        ) {
          return true;
        }
        if (subType === 'addition') {
          return step.details.simpleData[subType].elements.some(
            (element) =>
              element.argumentType === ELEMENT_TYPES.variable &&
              (element.value as TargetFieldVariable)?.value === variableName
          );
        } else {
          return [
            ...step.details.simpleData[subType].numeratorElements,
            ...step.details.simpleData[subType].denominatorElements,
          ].some(
            (element) =>
              element.argumentType === ELEMENT_TYPES.variable &&
              (element.value as TargetFieldVariable)?.value === variableName
          );
        }
      }

      case 'create_related_entity': {
        return (
          (step.details.newEntityOwnerSubType ===
            OWNER_SUBTYPES.FROM_VARIABLE &&
            step.details.newEntityOwnerVariable?.value === variableName) ||
          step.details.targetVariable?.value === variableName
        );
      }

      case 'delay':
      case 'goal': {
        return (
          step.details.valueOrigin === ValueOrigin.automation_variable &&
          step.details.variable?.value === variableName
        );
      }

      case 'schedule_activity': {
        return (
          isDeletedAssignedToVariable(variableName, step.details?.assignedTo) ||
          (step.details?.schedule?.type === SCHEDULE_TYPES.WITH_DELAY &&
            (isDeletedWithDelayDelayVariable(
              variableName,
              step.details?.schedule?.withDelay
            ) ||
              isDeletedWithDelayTimeOffsetVariable(
                variableName,
                step.details?.schedule?.withDelay
              ))) ||
          (step.details?.schedule?.type ===
            SCHEDULE_TYPES.ON_OR_AROUND_DATE_VARIABLE &&
            (isDeletedOnAndAroundDateVariable(
              variableName,
              step.details?.schedule?.onOrAroundDate
            ) ||
              isDeletedOnAndAroundDateTimeOffsetVariable(
                variableName,
                step.details?.schedule?.onOrAroundDate
              ) ||
              isDeletedOnAndAroundDateDateOffsetVariable(
                variableName,
                step.details?.schedule?.onOrAroundDate
              ))) ||
          isDeletedAssociationVariable(variableName, step.details) ||
          isDeletedTargetVariable(variableName, step.details)
        );
      }

      case 'start_automation': {
        if (step.details.automationVariable?.value === variableName) {
          return true;
        }

        return step.details.automationVariableOverrides.some(
          (automationVariableOverride) => {
            return automationVariableOverride.variableOverrides.some(
              (variableOverride) => {
                return (
                  (variableOverride.valueSource === 'automation_variable' &&
                    variableOverride.automationVariable?.value ===
                      variableName) ||
                  (variableOverride.valueSource ===
                    'automation_entity_variable_field' &&
                    variableOverride.automationEntityVariable?.value ===
                      variableName)
                );
              }
            );
          }
        );
      }
      case 'modify_automation':
      case 'archive_record': {
        return step.details.automationVariable?.value === variableName;
      }
      case 'search_records': {
        return step.details.destinationVariable?.value === variableName;
      }
      case 'loop': {
        return step.details?.forEachItem?.arrayVariable?.value === variableName;
      }

      default:
        return false;
    }
  });
};

export const removeDeletedVariableFromActions = (
  steps: ActionStep[],
  variable: UiVariable
) => {
  const variableName = variable.details.variable.name;
  const errorIds: string[] = [];

  if (variable && steps.length) {
    const nextSteps = steps.map((step) => {
      switch (step.type) {
        case 'modify_related_entities': {
          const { nextStep, nextErrorIds } =
            modifyRelatedEntitiesRemoveVariable(step, variableName, errorIds);

          errorIds.push(...nextErrorIds);

          return nextStep;
        }

        case 'change_field_value': {
          const { nextStep, nextErrorIds } = changeFieldValueRemoveVariable(
            step,
            variableName,
            errorIds
          );

          errorIds.push(...nextErrorIds);

          return nextStep;
        }

        case 'call_llm':
        case 'audio_transcription':
        case 'file_content_extraction': {
          const { nextStep, nextErrorIds } = removeLLMVariable(
            step,
            variableName
          );

          errorIds.push(...nextErrorIds);

          return nextStep;
        }

        case 'update_variable': {
          const { nextStep, nextErrorIds } = updateVariableRemoveVariable(
            step,
            variableName,
            errorIds
          );

          errorIds.push(...nextErrorIds);

          return nextStep;
        }

        case 'plugin_code_step':
        case 'code_step': {
          const { nextStep, nextErrorIds } = codeStepRemoveVariable(
            step,
            variableName,
            errorIds
          );

          errorIds.push(...nextErrorIds);

          return nextStep;
        }

        case 'math_operator': {
          const { nextStep, nextErrorIds } = mathOperatorsRemoveVariable(
            step,
            variableName
          );
          errorIds.push(...nextErrorIds);

          return nextStep;
        }

        case 'create_related_entity': {
          const { nextStep, nextErrorIds } = createRelatedEntityRemoveVariable(
            step,
            variableName,
            errorIds
          );
          errorIds.push(...nextErrorIds);

          return nextStep;
        }

        case 'goal':
        case 'delay': {
          const { nextStep, nextErrorIds } = delayRemoveVariable(
            step as DelayStep,
            variableName,
            errorIds
          );
          errorIds.push(...nextErrorIds);

          return nextStep;
        }

        case 'schedule_activity': {
          const { nextStep, nextErrorIds } = scheduleActivityRemoveVariable(
            step,
            variableName,
            errorIds
          );
          errorIds.push(...nextErrorIds);

          return nextStep;
        }

        case 'start_automation': {
          const { nextStep, nextErrorIds } = startAutomationStepRemoveVariable(
            step,
            variableName,
            errorIds
          );
          errorIds.push(...nextErrorIds);

          return nextStep;
        }

        case 'modify_automation': {
          const { nextStep, nextErrorIds } = modifyAutomationStepRemoveVariable(
            step,
            variableName,
            errorIds
          );
          errorIds.push(...nextErrorIds);

          return nextStep;
        }

        case 'archive_record': {
          const { nextStep, nextErrorIds } = archiveRecordStepRemoveVariable(
            step,
            variableName,
            errorIds
          );
          errorIds.push(...nextErrorIds);

          return nextStep;
        }

        case 'search_records': {
          const { nextStep, nextErrorIds } = searchRecordsStepRemoveVariable(
            step,
            variableName,
            errorIds
          );
          errorIds.push(...nextErrorIds);

          return nextStep;
        }

        case 'loop': {
          const { nextStep, nextErrorIds } = loopRemoveVariable(
            step,
            variableName,
            errorIds
          );
          errorIds.push(...nextErrorIds);

          return nextStep;
        }
        default:
          return step;
      }
    });

    return { nextSteps, errorIds };
  }

  return { nextSteps: steps, errorIds };
};
